import { VStack } from '@wegroup/design-system';
import React from 'react';
import { Car } from '../../../../../../../types/SMT';
import useFlowsAnswer from '../../../../hooks/useFlowsAnswer';
import useFlowsHelper from '../../../../hooks/useFlowsHelper';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import FlowsBoxQuestionDescription from '../../../ui/advisory-flow/components/FlowsQuestionDescription';
import FlowsQuestionText from '../../../ui/advisory-flow/components/FlowsQuestionText';
import NumberPlateInput from './NumberPlateInput';

interface Props {
  question: FlowsQuestion;
}

const NumberPlateInputComponent: React.FC<Props> = ({ question }) => {
  const {
    field: { value, onBlur },
  } = useFlowInputComponent({ question });
  const { getGroupByQuestionId } = useFlowsHelper();
  const { fillAnswers } = useFlowsAnswer();

  const handleVehicleFound = (vehicle: Car, numberPlate: string) => {
    const vehicleType = getVehicleTypeFromQuestionId(question.id);
    const vehicleQuestionMapping = getVehicleQuestionMapping(
      question.tag,
      vehicleType,
    );

    // Reset all answers
    const clearedAnswers = Object.values(vehicleQuestionMapping).map(
      (questionId) => ({
        id: questionId,
        answer: undefined,
        index: question.index,
        groupId: getGroupByQuestionId(question.id).id,
        tag: question.tag,
      }),
    );
    fillAnswers(clearedAnswers);

    // Prefill all answers
    const newAnswers = Object.entries(vehicleQuestionMapping)
      .map(([field, questionId]) => ({
        id: questionId,
        answer: vehicle[field],
        groupId: getGroupByQuestionId(question.id).id,
        index: question.index,
        prefilled: true,
        tag: question.tag,
      }))
      .filter(({ answer }) => !!answer);
    fillAnswers(newAnswers);

    onBlur({ name: question.id, value: numberPlate.toUpperCase() });
  };

  return (
    <VStack align="stretch">
      <FlowsQuestionText question={question} />
      <FlowsBoxQuestionDescription question={question} />
      <NumberPlateInput
        name={question.id}
        value={value}
        onChange={handleVehicleFound}
        isDisabled={question.rules.is_disabled}
      />
    </VStack>
  );
};

/**
 * Will return an object with `vehicleField: questionId` mapping
 */
const getVehicleQuestionMapping = (tagParam: string, vehicleType: string) => {
  const tag = tagParam.toLowerCase();
  return {
    brand: `${tag}.${vehicleType}_brand`,
    model: `${tag}.${vehicleType}_model`,
    version: `${tag}.${vehicleType}_version`,
    motor_type: `${tag}.${vehicleType}_motor_type`,
    kw: `${tag}.${vehicleType}_kw`,
    value: `${tag}.${vehicleType}_catalogue_value`,
    registration_first: `${tag}.${vehicleType}_registration_first`,
    invoice_value: `${tag}.${vehicleType}_invoice_value`,
    options_value: `${tag}.${vehicleType}_options_value`,
    seats: `${tag}.${vehicleType}_seats`,
    model_year: `${tag}.${vehicleType}_model_year`,
  };
};

const getVehicleTypeFromQuestionId = (questionId: string) =>
  // Example id: mobility.car_brand
  questionId?.split('.')[1].split('_')[0];

export default NumberPlateInputComponent;
